import { useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { browseList } from './api';
import { loadBrowseBatch, uniqueBrowseItems } from './browseStreamModel';

export function useBrowseStream(kind, args, identity, { enabled = true } = {}) {
  const client = useQueryClient();
  // Legacy numbered URLs open the stream from the beginning; never skip content.
  const { page: ignoredPage, ...inputs } = args;
  const family = kind === 'albums' ? 'reviewsAlbums' : 'browse-reviews';
  const query = useInfiniteQuery({
    queryKey: [family, 'stream60', identity, inputs],
    initialPageParam: { page: 1 },
    queryFn: ({ pageParam, signal }) =>
      loadBrowseBatch(kind, inputs, pageParam.page, pageParam.source, signal, browseList),
    getNextPageParam: (last) =>
      last.hasMore ? { page: last.pagination.currentPage + 1, source: last.source } : undefined,
    enabled,
    retry: false,
    staleTime: Infinity,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  });
  const pages = query.data?.pages;
  const view = inputs.view;
  const excludedIds = JSON.stringify(inputs.windowExcludedIds || []);
  // Keep the derived list stable between renders. Consumers may initialise an
  // editor from it; a fresh array would continuously retrigger those effects.
  const data = useMemo(
    () =>
      pages
        ? {
            albums: uniqueBrowseItems(
              kind,
              { view, windowExcludedIds: JSON.parse(excludedIds) },
              pages,
            ),
            pagination: pages.at(-1).pagination,
          }
        : undefined,
    [pages, kind, view, excludedIds],
  );
  return {
    ...query,
    data,
    batchCount: pages?.length || 0,
    // Additional-page feedback lives at the bottom, without pushing old cards.
    isError: query.isError && !query.isFetchNextPageError,
    isFetching: query.isFetching && !query.isFetchingNextPage,
    busy: query.isFetching,
    initialLoading: query.isPending && !query.data,
    refresh: () => client.invalidateQueries({ queryKey: [family, 'stream60', identity] }),
  };
}
